// @ts-check
export class UtmParser {
	STORAGE_KEY = 'utm_params';
	SEARCH_SITES = [
		'google.com',
		'bing.com',
		'yahoo.com',
		'ask.com',
		'aol.com',
		'avg.com',
		'baidu.com',
		'msn.com',
		'yandex.com',
	];
	SOCIAL_SITES = [
		'linkedin.com',
		'twitter.com',
		'instagram.com',
		'yelp.com',
		'pocket.com',
		'weebly.com',
		'blogger.com',
		'reddit.com',
		'youtube.com',
		'tumblr.com',
		'plus.google.com',
	];

	/**
	 * @param {string} urlStr The string of the URL to handle parsing of.
	 */
	constructor(urlStr) {
		this.url = new URL(urlStr);
	}

	/** @returns {Record<string, string> | undefined} */
	get utmParamObj() {
		const strVal = sessionStorage.getItem(this.STORAGE_KEY) ?? undefined;
		return !strVal ? undefined : JSON.parse(strVal);
	}
	set utmParamObj(obj) {
		const strVal = JSON.stringify(obj);
		sessionStorage.setItem(this.STORAGE_KEY, strVal);
	}
	/** @param {Window} windowObject The provided window in context. */
	initUtmParamObj = (windowObject) => {
		if (!windowObject || this.utmParamObj) {
			// Escape gatsby build, or we have a stored object, do no init.
			return;
		}

		// A list of param keys that require 'utm_' when being converted back to string format.
		const paramKeyList = [];
		/** @type {Record<string, string>} */
		const paramObj = Object.fromEntries(this.url.searchParams.entries()) ?? {};

		this.handleSource(windowObject, paramObj, paramKeyList);
		this.handleMedium(paramObj, paramKeyList);

		this.handleSpecific(paramObj, 'gclid', 'google.com');
		this.handleSpecific(paramObj, 'msclkid', 'bing.com');

		this.setParamObjRawVal(paramObj, paramKeyList);

		this.utmParamObj = paramObj;
	};

	/** Search for given source, or append our own. */
	handleSource = (
		/** @type {Window} */ windowObject,
		/** @type {Record<string, string>} */ paramObj,
		/** @type {string[]} */ paramKeyList
	) => {
		const foundSource = this.getUtmParam('source');
		if (!foundSource) {
			if (windowObject.document.referrer) {
				paramObj.source = this.url.hostname;
			} else {
				paramObj.source = 'direct';
				paramKeyList.push('source');
			}
		}
	}

	/** Checks for known Mediums, and classify. */
	handleMedium = (
		/** @type {Record<string, string>} */ paramObj,
		/** @type {string[]} */ paramKeyList
	) => {
		// document.referrer does not always get passed depending on referring site headers & security
		// Source: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Referrer-Policy
		if (!paramObj.medium) {
			if (!paramObj.medium && !window.document.referrer) {
				paramObj.medium = 'not_provided';
			} else if (this.url.hostname === this.SEARCH_SITES.find((domain) => domain === this.url.hostname)) {
				paramObj.medium = 'organic_search';
			} else if (this.url.hostname === this.SOCIAL_SITES.find((domain) => domain === this.url.hostname)) {
				paramObj.medium = 'social';
			} else {
				paramObj.medium = 'referral';
			}
		}
		paramKeyList.push('medium');
	}

	/** Searches the query for a specific key, then adds a source, medium, and clickId to param obj if found. */
	handleSpecific = (
		/** @type {Record<string, string>} */ paramObj,
		/** @type {string} */ key,
		thenSource
	) => {
		if (key in paramObj) {
			paramObj.source = thenSource;
			paramObj.medium = 'cpc';
			paramObj.clickId = paramObj[key];
		}
	}

	/**
	 * Consults paramKeyList to update inner "key"s to include a 'utm_' prefix,
	 * then stringifies them and adds it as a "raw" field to the param object.
	 */
	setParamObjRawVal = (
		/** @type {Record<string, string>} */ paramObj,
		/** @type {string[]} */ paramKeyList
	) => {
		const toWrite = Object.assign({}, paramObj);
		Object.entries(toWrite).forEach(([key, val]) => {
			if (paramKeyList.includes(key)) {
				delete toWrite[key];
				toWrite['utm_' + key] = val;
			}
		});
		const raw = new URLSearchParams(toWrite);
		paramObj['raw'] = raw.toString();
	}

	/**
	 * Gets a specific UTM parameter when searching. Validates an optional 'utm_' prefix.
	 * @param {string} key The identifier of this param key.
	 * @param {string} [defaultVal] The value to provide if omitted from {this.rl}
	 * @returns {string | undefined} The found value, or the default (or undefined) value.
	 */
	getUtmParam = (key, defaultVal) => {
		return this.utmParamObj?.[key] ?? this.utmParamObj?.['utm_' + key] ?? defaultVal;
	};
}
